import React, { useCallback, useRef, useState } from "react";
import { ReactComponent as SendIcon } from "assets/images/svgs/sendSvg.svg";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import useAmbassadorCommunityHooks from "./useAmbassadorCommunityHooks";

type AmbassadorMessageInputProps = {
  eventId: number | null;
  messages: MessageDTO[];
  setMessages: (updater: any) => void;
  onMessageAppended: (msg: MessageDTO) => void;
};

const AmbassadorMessageInput: React.FC<AmbassadorMessageInputProps> = ({ 
  eventId, 
  messages, 
  setMessages, 
  onMessageAppended 
}) => {
  const { sendMessageToCommunity } = useAmbassadorCommunityHooks();
  const [value, setValue] = useState("");
  const isSendingRef = useRef(false);

  const handleSend = useCallback(async () => {
    if (!eventId) return;
    const trimmed = value.trim();
    if (!trimmed || isSendingRef.current) return;
    isSendingRef.current = true;

    const tempId = `temp-${Date.now()}`;
    const optimistic = new MessageDTO({
      id: tempId as any,
      message: trimmed,
      sentByMe: true,
      eventId: eventId,
      createdAt: new Date().toISOString(),
    });
    onMessageAppended(optimistic);
    setValue("");

    try {
      const withOptimistic = [...messages, optimistic];
      await sendMessageToCommunity(eventId, optimistic, withOptimistic, setMessages);
    } catch (e) {
      setMessages((prev: MessageDTO[]) => prev.filter((m) => m.id !== (tempId as any)));
    } finally {
      isSendingRef.current = false;
    }
  }, [eventId, messages, onMessageAppended, sendMessageToCommunity, setMessages, value]);

  return (
    <form
      className="flex items-center p-3 min-w-0 flex-shrink-0"
      onSubmit={(e) => {
        e.preventDefault();
        handleSend();
      }}
    >
      <div className="relative flex-1 min-w-0 w-full">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Type a message"
          className="w-full h-[80px] p-2 pl-4 pr-10 border-2 border-borderColor rounded-lg outline-none min-w-0"
          maxLength={1000}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2 flex-shrink-0">
          <SendIcon />
        </button>
      </div>
    </form>
  );
};

export default React.memo(AmbassadorMessageInput);
